import { Avatar, Box, Button, Center, Flex, Loader, Paper, SimpleGrid, Text, Title } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import trpc from "@/shared/utils/trpc";
import { getAmcLogoUrl } from "@/shared/utils/amcLogoMap";
import { UpdateSchemesModal, type SchemeItem } from "./UpdateSchemesModal";

// ─── Types ───────────────────────────────────────────────────────────────────

type GoalTermData = {
  term: string;
  label: string;
  subtitle: string;
  schemes: SchemeItem[];
  loading: boolean;
};

// ─── Config ───────────────────────────────────────────────────────────────────

// TODO: Replace term values with actual values from master_scheme_table.term
// and SuggestedScheme.category once confirmed from the database.
const GOAL_TERMS: Pick<GoalTermData, "term" | "label" | "subtitle">[] = [
  { term: "Short_Term", label: "Short term goal", subtitle: "0 to 2.5 years" },
  { term: "Medium_Term", label: "Medium term goal", subtitle: "2.5 to 5 years" },
  { term: "Long_Term", label: "Long term goal", subtitle: "5 years and beyond" },
];

// ─── GoalCard ─────────────────────────────────────────────────────────────────

function GoalCard({ data, onUpdate }: { data: GoalTermData; onUpdate: (term: string) => void }) {
  const { label, subtitle } = data;

  return (
    <Paper
      radius="md"
      mih={452}
      style={{
        border: "1px solid var(--mantine-color-gray-3)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box p="lg" style={{ flex: 1 }}>
        <Title order={4} fw={600} mb={2}>
          {label}
        </Title>
        <Text fz="xs" c="slate.5" mb="md">
          {subtitle}
        </Text>

        {data.loading ? (
          <Center py="xl">
            <Loader size="sm" type="dots" />
          </Center>
        ) : data.schemes.length === 0 ? (
          <Text fz="sm" c="slate.4" py="md">
            No schemes configured yet.
          </Text>
        ) : (
          <Flex direction="column" gap="xs">
            {data.schemes.map((scheme) => {
              const initials = scheme.name
                .split(" ")
                .slice(0, 2)
                .map((w) => w[0])
                .join("")
                .toUpperCase();
              const logoUrl = getAmcLogoUrl(scheme.scheme_amc_name);
              return (
                <Paper
                  key={scheme.scheme_bse_code}
                  radius="md"
                  p="sm"
                  style={{ border: "1px solid var(--mantine-color-gray-3)" }}
                >
                  <Flex align="center" gap="sm">
                    <Avatar
                      src={logoUrl}
                      size={36}
                      radius="xl"
                      color="primary"
                      variant="filled"
                      style={{ flexShrink: 0 }}
                    >
                      {initials}
                    </Avatar>
                    <Text fz="sm" style={{ lineHeight: 1.3 }}>
                      {scheme.name}
                    </Text>
                  </Flex>
                </Paper>
              );
            })}
          </Flex>
        )}
      </Box>

      <Box p="lg" pt={0}>
        <Button
          fullWidth
          variant="outline"
          color="primary"
          leftSection={<IconPencil size={14} />}
          onClick={() => onUpdate(data.term)}
        >
          Update Schemes
        </Button>
      </Box>
    </Paper>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function fetchTermData(term: string, onResult: (schemes: SchemeItem[]) => void) {
  trpc.schemes.getSuggestedSchemes
    .query({ term })
    .then((res) => onResult(res.schemes))
    .catch(() => onResult([]));
}

export default function GoalSchemesPage() {
  const [termData, setTermData] = useState<GoalTermData[]>(
    GOAL_TERMS.map((t) => ({ ...t, schemes: [], loading: true }))
  );
  const [modalTerm, setModalTerm] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    GOAL_TERMS.forEach(({ term }) => {
      fetchTermData(term, (schemes) => {
        if (cancelled) return;
        setTermData((prev) => prev.map((d) => (d.term === term ? { ...d, schemes, loading: false } : d)));
      });
    });
    return () => { cancelled = true; };
  }, []);

  const activeTerm = termData.find((d) => d.term === modalTerm);

  const handleSaved = (term: string) => {
    setTermData((prev) => prev.map((d) => (d.term === term ? { ...d, loading: true } : d)));
    fetchTermData(term, (schemes) => {
      setTermData((prev) => prev.map((d) => (d.term === term ? { ...d, schemes, loading: false } : d)));
    });
  };

  return (
    <Flex direction="column" w="100%" p="xl">
      <Title order={2} fw={700} mb="xl">
        Goal & Schemes
      </Title>

      <SimpleGrid cols={3} spacing="lg">
        {termData.map((data) => (
          <GoalCard key={data.term} data={data} onUpdate={setModalTerm} />
        ))}
      </SimpleGrid>

      {activeTerm && (
        <UpdateSchemesModal
          opened={!!modalTerm}
          term={activeTerm.term}
          termLabel={activeTerm.label}
          currentBseCodes={activeTerm.schemes.map((s) => s.scheme_bse_code)}
          onClose={() => setModalTerm(null)}
          onSaved={handleSaved}
        />
      )}
    </Flex>
  );
}
